import { IconButton, Stack, Tooltip, Typography, styled } from '@mui/material';
import { Doc } from 'src/@types/docs';
import { useConfirmationModal } from 'src/components/ConfirmationModal';
import Iconify from 'src/components/iconify';
import { DocAvatar } from 'src/features/doc/DocAvatar';
import { useLocales } from 'src/locales';
import { useDeletePemanentlyDocMutation } from './useDeletePemanentlyDocMutation';
import { useRestoreDocMutation } from './useRestoreDocMutation';

type Props = {
  doc: Doc;
};

export const DocsTrashItem = ({ doc }: Props) => {
  const { translate } = useLocales();
  const { confirm } = useConfirmationModal();

  const { restoreDoc, isRestoringDoc } = useRestoreDocMutation();
  const { deletePermanentlyDoc, isDeletingDoc } =
    useDeletePemanentlyDocMutation();

  const handleRestoreDoc = async () => {
    restoreDoc(doc?._id);
  };

  const handleDeleteDocFromTrash = async () => {
    if (
      !(await confirm({
        title: translate('shared.confirmation.are_you_sure_title'),
        message: translate(
          'collaborate.docs.trash.delete_permanently_message',
          {
            docTitle: doc.title,
          },
        ),
      }))
    )
      return;
    deletePermanentlyDoc(doc._id);
  };

  return (
    <DocsTrashItemStyled>
      <Stack direction="row" gap={1} alignItems="center">
        <DocAvatar key={doc._id} icon={doc.icon?.value} />
        <Typography variant="body2">{doc.title}</Typography>
      </Stack>
      <Stack direction="row" spacing={1}>
        <Tooltip title={translate('shared.buttons.restore')} placement="right">
          <IconButton disabled={isRestoringDoc} onClick={handleRestoreDoc}>
            <Iconify icon="tabler:arrow-back-up" width={16} height={16} />
          </IconButton>
        </Tooltip>

        <Tooltip title={translate('shared.buttons.delete')} placement="right">
          <IconButton
            disabled={isDeletingDoc}
            onClick={handleDeleteDocFromTrash}
          >
            <Iconify icon="tabler:trash" width={16} height={16} />
          </IconButton>
        </Tooltip>
      </Stack>
    </DocsTrashItemStyled>
  );
};

const DocsTrashItemStyled = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1.5),
  border: '1px solid',
  borderColor: theme.palette.divider,
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.text.secondary,
}));
